
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../LoadingSpinner';
import OrganizationLogo from '../OrganizationLogo';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft } from 'lucide-react';

const DEMO_EMAIL = 'demo@sme.ng';
const DEMO_PASSWORD = 'demo123';
// Removed hardcoded admin credentials for security

const LOGIN_ERROR_KEY = 'login_error_temp';

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Initialize error from sessionStorage to survive remounts
  const [error, setError] = useState(() => {
    const stored = sessionStorage.getItem(LOGIN_ERROR_KEY);
    return stored || '';
  });
  const [demoLoading, setDemoLoading] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [organizationName, setOrganizationName] = useState('Growth Energy');
  const [organizationLogo, setOrganizationLogo] = useState<string | null>(null);

  const { login, isLoading } = useAuth();

  // Custom setError that persists to sessionStorage
  const setErrorPersistent = (msg: string) => {
    setError(msg);
    if (msg) {
      sessionStorage.setItem(LOGIN_ERROR_KEY, msg);
    } else {
      sessionStorage.removeItem(LOGIN_ERROR_KEY);
    }
  };


  useEffect(() => {
    const fetchOrganization = async () => {
      const orgId = localStorage.getItem('organizationId');
      if (orgId) {
        const { supabase } = await import('@/integrations/supabase/client');

        const { data, error } = await supabase
          .from('organizations')
          .select('name, logo_url')
          .eq('id', orgId)
          .single();

        if (!error && data) {
          setOrganizationName(data.name);
          setOrganizationLogo(data.logo_url);
        }
      }
    };

    fetchOrganization();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorPersistent('');

    // Client-side validation
    if (!email || !password) {
      setErrorPersistent('Please fill in all fields');
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorPersistent('Please enter a valid email address');
      return;
    }

    const result = await login(email, password);

    if (!result || !result.success) {
      const errorMsg = result?.error || 'Login failed. Please try again.';
      setErrorPersistent(errorMsg);
    } else {
      // Clear error on success
      setErrorPersistent('');
    }
  };

  const handleDemoLogin = async () => {
    setErrorPersistent('');
    setDemoLoading(true);
    setEmail(DEMO_EMAIL);
    setPassword(DEMO_PASSWORD);
    const result = await login(DEMO_EMAIL, DEMO_PASSWORD);
    if (!result.success) {
      setErrorPersistent(result.error || 'Demo login failed. Please try again.');
    } else {
      setErrorPersistent('');
    }
    setDemoLoading(false);
  };

  const handleAdminLogin = () => {
    setErrorPersistent('');
    // For security, admin users must manually enter their credentials
    setEmail('admin@growth-energy.tz');
    setPassword('');
  };

  const showLoading = isLoading || demoLoading || adminLoading;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-primary/5 to-secondary/10 dark:from-background dark:via-primary/10 dark:to-secondary/5">
      <div className="w-full max-w-md space-y-8 animate-in fade-in-0 duration-700">
        {/* Back Button */}
        <button
          onClick={() => navigate('/preauth')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to organizations</span>
        </button>

        {/* Header */}
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="relative group">
              <div className="transition-all duration-300 group-hover:scale-105">
                <OrganizationLogo
                  logoUrl={organizationLogo}
                  organizationName={organizationName}
                  size="lg"
                  className="shadow-2xl"
                />
              </div>
              <div className="absolute -inset-2 bg-gradient-to-br from-primary to-primary/60 rounded-3xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent rounded-3xl animate-pulse"></div>
            </div>
          </div>
          
          <div className="space-y-3">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">Welcome Back</h1>
            <p className="text-muted-foreground text-lg">Growth Energy Monitoring Platform</p>
            {organizationName && (
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 text-primary text-sm font-medium shadow-sm">
                <div className="w-2 h-2 bg-primary rounded-full mr-2 animate-pulse"></div>
                {organizationName}
              </div>
            )}
          </div>
        </div>

        {/* Login Card */}
        <Card className="border-0 shadow-2xl bg-card/95 backdrop-blur-lg border border-border/50 animate-in slide-in-from-bottom-4 duration-700 delay-200">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 text-sm text-destructive bg-transparent border border-destructive/30 rounded-xl">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-foreground">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 px-4 bg-background/50 border-border focus:bg-background focus:border-primary focus:ring-primary/20 rounded-xl transition-all duration-200"
                  placeholder="your.email@example.com"
                  disabled={showLoading}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-foreground">
                    Password
                  </Label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary hover:text-primary/80 transition-colors duration-200"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 px-4 bg-background/50 border-border focus:bg-background focus:border-primary focus:ring-primary/20 rounded-xl transition-all duration-200"
                  placeholder="Enter your password"
                  disabled={showLoading}
                />
              </div>

              <div className="space-y-4">
                <Button
                  type="submit"
                  disabled={showLoading}
                  className="w-full h-12 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold rounded-xl shadow-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-primary/25"
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <LoadingSpinner size="sm" />
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    'Sign In'
                  )}
                </Button>

                {/* Quick Access - Only shown for Fumba Demo organization */}
                {organizationName.toLowerCase().includes('fumba') && (
                  <>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-3 bg-card text-muted-foreground">Quick Access</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        type="button"
                        onClick={handleDemoLogin}
                        disabled={showLoading}
                        variant="outline"
                        className="h-12 border-2 border-secondary/40 text-secondary-foreground hover:bg-secondary/20 hover:border-secondary/60 rounded-xl transition-all duration-200 group"
                      >
                        {demoLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <span className="group-hover:scale-105 transition-transform duration-200">Demo</span>
                        )}
                      </Button>

                      <Button
                        type="button"
                        onClick={handleAdminLogin}
                        disabled={showLoading}
                        variant="outline"
                        className="h-12 border-2 border-accent/40 text-accent-foreground hover:bg-accent/20 hover:border-accent/60 rounded-xl transition-all duration-200 group"
                      >
                        <span className="group-hover:scale-105 transition-transform duration-200">Admin Login</span>
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Demo Credentials - Only shown for Fumba Demo organization */}
        {organizationName.toLowerCase().includes('fumba') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs animate-in fade-in-0 duration-700 delay-500">
            <div className="p-4 bg-secondary/10 border border-secondary/20 rounded-xl backdrop-blur-sm hover:bg-secondary/15 transition-colors duration-200">
              <h4 className="font-medium text-secondary-foreground mb-2 flex items-center">
                <div className="w-2 h-2 bg-secondary rounded-full mr-2"></div>
                Demo Account
              </h4>
              <div className="text-muted-foreground space-y-1">
                <div>Email: demo@sme.ng</div>
                <div>Password: demo123</div>
              </div>
            </div>
            <div className="p-4 bg-accent/10 border border-accent/20 rounded-xl backdrop-blur-sm hover:bg-accent/15 transition-colors duration-200">
              <h4 className="font-medium text-accent-foreground mb-2 flex items-center">
                <div className="w-2 h-2 bg-accent rounded-full mr-2"></div>
                Administrator
              </h4>
              <div className="text-muted-foreground space-y-1">
                <div>Contact admin for credentials</div>
                <div>Secure authentication required</div>
              </div>
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="text-center space-y-3 animate-in fade-in-0 duration-700 delay-600">
          <div className="p-4 bg-muted/50 border border-border/50 rounded-xl backdrop-blur-sm">
            <p className="text-sm text-muted-foreground">
              <strong>Don't have an account yet?</strong>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Please contact your organization administrator to get access to the platform.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center animate-in fade-in-0 duration-700 delay-700">
          <p className="text-xs text-muted-foreground">
            Powered by Growth Energy - Member of Solio Group
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
